import { Instance, types } from "mobx-state-tree";

import { ShareStatusEnum } from "Enumerations";
import { TWidgetFilterStore } from "../Widgets/WidgetFilterStore";

export interface IWidgetConfig {
    title?: string;
    isConfigSummaryOpen?: boolean;
}

export interface IWidgetFilterProps<WT> {
    widgetFilterStore?: TWidgetFilterStore;
    widgetConfig?: WT;
    setInvalidFilterMessage?: (message: string) => void;
    cardRegistryId?: string;
}

export const Layout = types.model({
    key: types.identifier,
    breakpointName: types.string,
    w: types.integer,
    h: types.integer,
    x: types.integer,
    y: types.integer
});

export const Widget = types.model({
    /**
     * Uniquely identify a widget on the dashboard that can have multiple widgets of same type i.e. of same widgetRegistryId
     */
    viewWidgetKey: types.identifier,
    /**
     * Uniquely identify a widget in the Widget registry.
     */
    widgetRegistryId: types.string,
    /**
     * Layout models of widget for various screen sizes.
     */
    layouts: types.array(Layout),
    /**
     * Widget configuration settings.
     */
    config: types.maybe(types.string)
});

export const Subview = types.model({
    key: types.identifier,
    title: types.string,
    path: types.string,
    cards: types.array(Widget)
});

export type ISubview = Instance<typeof Subview>;

export interface ILayout {
    key: string;
    breakpointName: string;
    w: number;
    h: number;
    x: number;
    y: number;
}

export interface IWidget {
    viewWidgetKey: string;
    widgetRegistryId: string;
    layouts: ILayout[];
    config?: string;
}

export interface IViewConfig {
    key: string;
    title: string;
    path: string;
    sharedOwner?: string;
    sharedkey?: string;
    shareStatus?: ShareStatusEnum;
    subviews?: ISubview[];
    //The cards property is a deprecated property and should not be used. Use subviews instead. This property will be removed in the future.
    cards?: IWidget[];
}

export const View = types
    .model({
        key: types.identifier,
        title: types.string,
        path: types.string,
        sharedOwner: types.maybe(types.string),
        sharedkey: types.maybe(types.string),
        shareStatus: types.maybe(types.number),
        description: types.maybe(types.string),
        editors: types.maybe(types.array(types.string)),
        tags: types.maybe(types.array(types.string)),
        subviews: types.maybe(types.array(Subview)),
        //The cards property is a deprecated property and should not be used. Use subviews instead. This property will be removed in the future.
        cards: types.maybe(types.array(Widget)),
        temporal: types.optional(types.boolean, false)
    })
    .actions((self) => ({
        saveToDashboard() {
            self.temporal = false;
        }
    }));

export type IView = Instance<typeof View>;

const DashboardSettingsModel = types.model({
    userId: types.optional(types.string, ""),
    views: types.optional(types.array(View), []),
    displayViewGalleryPopover: types.optional(types.boolean, false),
    displayRedirectionBanner: types.optional(types.boolean, false),
    displayDashboardTour: types.optional(types.boolean, true),
    displayNewUserDashboardTour: types.optional(types.boolean, true)
});

export const DashboardSettings = types.snapshotProcessor(DashboardSettingsModel, {
    postProcessor: (snapshot) => ({
        userId: snapshot.userId,
        views: snapshot.views.filter((view) => view.temporal !== true),
        displayViewGalleryPopover: snapshot.displayViewGalleryPopover,
        displayRedirectionBanner: snapshot.displayRedirectionBanner,
        displayDashboardTour: snapshot.displayDashboardTour,
        displayNewUserDashboardTour: snapshot.displayNewUserDashboardTour
    })
});

export type IDashboardSettings = Instance<typeof DashboardSettings>;

export interface IViewNavigationState {
    isPageEditPreserved?: boolean;
}

export const DashboardAppConfig = types.model({
    userSetting: DashboardSettings
});

export type IDashboardAppConfig = Instance<typeof DashboardAppConfig>;
